import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, TextField, Button, Chip, Autocomplete, CircularProgress, Typography } from '@mui/material';
import { Close } from '@mui/icons-material';
import { WizardAccordion } from '../../components/WizardAccordion';
import { ImageThumbnail } from '../../components/ImageThumbnail';
import { DynamicTextFields } from '../../components/DynamicTextFields';
import { CollectionMembershipSelector } from '../../components/CollectionMembershipSelector';
import { useCatalogKeywords } from '../../hooks/useCatalogKeywords';
import { useCatalog } from '../../hooks/useCatalog';
import { useUpload } from '../../hooks/useUpload';
import { useWizardCompletion } from '../../hooks/useWizardCompletion';
import { useAutosaveStatus } from '../../hooks/useAutosaveStatus';
import { useRegisterSaveNow } from '../../hooks/useRegisterSaveNow';
import { useKeywordSuggestions } from '../../hooks/useKeywordSuggestions';
import { logger } from '../../utils/logger';
import { en } from '../../translations/en';

/** Merge old per-section notes into a single 'notes' key (one-way migration). */
const mergeOldNotes = (stepNotes: Record<string, string[]>): Record<string, string[]> => {
  if (stepNotes.notes) return stepNotes;
  const oldKeys = Object.keys(stepNotes).filter(k => k !== 'notes');
  const merged = oldKeys.flatMap(k => stepNotes[k] || []).filter(Boolean);
  return merged.length > 0 ? { notes: merged } : {};
};

interface CatalogKeywordsTabProps {
  activeStep?: number;
  catalogItemId?: string;
  ownerId?: string | null;
  /**
   * When true, the trailing Additional Notes accordion is hidden. Used by the
   * /edit-object redesign where step-level notes have been retired; the
   * legacy /upload-object wizard omits this prop and continues to show
   * the section. `stepNotes` still loads/saves through the passthrough so
   * any historic data is preserved on round-trip.
   */
  hideAdditionalNotes?: boolean;
  /**
   * When true, the Keywords accordion is hidden because Keywords now lives
   * inside the Artwork Details step (`KeywordsSection`). The keyword list
   * still loads and saves through here so the shared backing record stays
   * round-tripped intact even when the UI is suppressed.
   */
  hideKeywords?: boolean;
}

export const CatalogKeywordsTab = ({ catalogItemId, ownerId, hideAdditionalNotes, hideKeywords }: CatalogKeywordsTabProps) => {
  const { currentItem } = useCatalog();
  const { catalogId } = useUpload();
  const effectiveCatalogId = catalogItemId || catalogId || currentItem?.catalogItemId;
  const { loadKeywords, updateKeywords } = useCatalogKeywords(effectiveCatalogId, ownerId);
  const { markStepComplete } = useWizardCompletion();
  const { setStatus, setLastSaveTime } = useAutosaveStatus();
  // Pass ownerId so delegates see the catalog owner's past keywords (#952)
  const { suggestions, isLoading: suggestionsLoading } = useKeywordSuggestions(ownerId ?? undefined);

  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  // Initialize with the default state JSON so unmount doesn't trigger unwanted saves.
  // This matches the exact structure that savKeywords() will produce with default state.
  const lastSaveRef = useRef<string>(JSON.stringify({
    aliCatalogNumber: '',
    customCatalogNumbers: [],
    keywords: [],
    stepNotes: {}
  }));
  const loadedCatalogIdRef = useRef<string | null>(null);
  // Autosave guard — engaged from first render to protect against
  // debounce/unmount-flush firing before load completes (sibling regression
  // of DA-55L.2499).
  const [isLoadingInitialData, setIsLoadingInitialData] = useState(true);
  const isLoadingInitialDataRef = useRef(true);
  useEffect(() => { isLoadingInitialDataRef.current = isLoadingInitialData; }, [isLoadingInitialData]);

  // Load-failure guard — mirrors DescribeTab.hasError (GL#40).
  // When the initial GET fails, we hold all write paths blocked so that a
  // subsequent debounce or unmount-flush cannot write blank defaults over the
  // persisted row.
  const [hasError, setHasError] = useState(false);
  const hasErrorRef = useRef(false);
  useEffect(() => { hasErrorRef.current = hasError; }, [hasError]);
  const [aliCatalogNumber, setAliCatalogNumber] = useState('');
  const [customCatalogNumbers, setCustomCatalogNumbers] = useState<string[]>([]);
  const [keywords, setKeywords] = useState<string[]>([]);
  const [keywordInput, setKeywordInput] = useState('');
  const [stepNotes, setStepNotes] = useState<Record<string, string[]>>({});

  // Load existing keywords on mount or when catalogId changes
  useEffect(() => {
    // Only load if we haven't already loaded this specific catalogId
    if (effectiveCatalogId && loadedCatalogIdRef.current !== effectiveCatalogId) {
      loadedCatalogIdRef.current = effectiveCatalogId;
      setIsLoadingInitialData(true);
      loadKeywords().then((data) => {
        if (data) {
          setAliCatalogNumber(data.aliCatalogNumber || '');
          setCustomCatalogNumbers(data.customCatalogNumbers || []);
          setKeywords(data.keywords || []);
          setStepNotes(mergeOldNotes(data.stepNotes || {}));

          // Update completion tracking after data loads
          const hasInventoryNumber = (data.customCatalogNumbers || []).length > 0;
          const hasKeywords = (data.keywords || []).length > 0;
          markStepComplete('catalogKeywords', 0, hasInventoryNumber);
          markStepComplete('catalogKeywords', 1, hasKeywords);
        }
        setIsLoadingInitialData(false);
      }).catch((err) => {
        // Load failed — set hasError so all write paths stay blocked (GL#40).
        logger.error('CatalogKeywordsTab: Failed to load keywords', err);
        setHasError(true);
        setIsLoadingInitialData(false);
      });
    } else if (!effectiveCatalogId) {
      setIsLoadingInitialData(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveCatalogId]);

  // Completion tracking function (for inventory number and keywords only)
  const updateCompletionTracking = useCallback(() => {
    // Step 0: Inventory Number
    markStepComplete('catalogKeywords', 0,
      customCatalogNumbers.length > 0
    );

    // Step 1: Keywords
    markStepComplete('catalogKeywords', 1, keywords.length > 0);

    // Note: Step 2 (Collection Catalogs) is tracked separately in handleCatalogChange
  }, [markStepComplete, customCatalogNumbers, keywords]);

  // Immediate completion tracking when keywords or inventory numbers change
  // This ensures checkmarks update immediately, not just on save
  useEffect(() => {
    markStepComplete('catalogKeywords', 0, customCatalogNumbers.length > 0);
    markStepComplete('catalogKeywords', 1, keywords.length > 0);
  }, [keywords, customCatalogNumbers, markStepComplete]);

  // Handler for catalog membership changes - stable callback to prevent infinite loops
  const handleCatalogChange = useCallback((catalogIds: string[]) => {
    // Update completion for Collection Catalogs step directly
    markStepComplete('catalogKeywords', 2, catalogIds.length > 0);
  }, [markStepComplete]);

  // Smart auto-save function
  const saveKeywords = useCallback(async () => {
    // Guard: never save during initial load OR after a load failure (GL#40).
    if (!effectiveCatalogId || isLoadingInitialData || hasError) {
      return;
    }

    const keywordData = {
      aliCatalogNumber,
      customCatalogNumbers,
      keywords,
      stepNotes
    };

    const currentDataString = JSON.stringify(keywordData);
    if (currentDataString === lastSaveRef.current) {
      return;
    }

    setStatus('saving');
    try {
      const result = await updateKeywords(keywordData);

      // Update local state with generated catalog number if available
      if (result.aliCatalogNumber && !aliCatalogNumber) {
        setAliCatalogNumber(result.aliCatalogNumber);
        // Update the data string to include the new catalog number
        const updatedData = { ...keywordData, aliCatalogNumber: result.aliCatalogNumber };
        lastSaveRef.current = JSON.stringify(updatedData);
      } else {
        lastSaveRef.current = currentDataString;
      }

      // Update completion tracking
      updateCompletionTracking();

      setStatus('saved');
      setLastSaveTime(new Date());
    } catch (error) {
      setStatus('error');
      logger.error('CatalogKeywordsTab - Failed to save keywords', error);
    }
  }, [effectiveCatalogId, isLoadingInitialData, hasError, updateKeywords, aliCatalogNumber, customCatalogNumbers, keywords, stepNotes, updateCompletionTracking, setStatus, setLastSaveTime]);

  // Ref always points to the latest save function — avoids stale closure in setTimeout
  const saveKeywordsRef = useRef(saveKeywords);
  useEffect(() => { saveKeywordsRef.current = saveKeywords; }, [saveKeywords]);
  // Let the wizard "Finished" button flush this tab's pending save.
  useRegisterSaveNow(saveKeywordsRef, saveTimeoutRef);

  const debouncedSave = useCallback(() => {
    // Signal immediately that there are unsaved edits — clears "All changes saved" (GL#40 Part 1).
    setStatus('dirty');
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => saveKeywordsRef.current(), 2000);
  }, [setStatus]); // setStatus is stable from context

  // Cleanup timeout and flush any pending save on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
      // Never flush during initial load or after load failure — would wipe row (GL#40).
      if (isLoadingInitialDataRef.current || hasErrorRef.current) return;
      try {
        saveKeywordsRef.current()?.catch((err: unknown) =>
          logger.error('Flush save failed on unmount', err)
        );
      } catch (err) {
        logger.error('Flush save failed on unmount (sync)', err);
      }
    };
  }, []);

  const addKeyword = () => {
    const parts = keywordInput.split(',').map((p) => p.trim()).filter(Boolean);
    if (parts.length === 0) return;

    const newKeywords = parts.reduce((acc, part) => {
      if (!acc.some((k) => k.toLowerCase() === part.toLowerCase())) {
        return [...acc, part];
      }
      return acc;
    }, keywords);

    if (newKeywords.length !== keywords.length) {
      setKeywords(newKeywords);
      debouncedSave();
    }
    setKeywordInput('');
  };

  const removeKeyword = (index: number) => {
    setKeywords(keywords.filter((_, i) => i !== index));
    debouncedSave();
  };

  // Retry effect: when hasError is cleared and loadedCatalogIdRef is null,
  // re-run the load (mirrors DescribeTab's retry pattern — GL#40).
  useEffect(() => {
    if (!hasError && loadedCatalogIdRef.current === null && effectiveCatalogId) {
      loadedCatalogIdRef.current = effectiveCatalogId;
      setIsLoadingInitialData(true);
      loadKeywords().then((data) => {
        if (data) {
          setAliCatalogNumber(data.aliCatalogNumber || '');
          setCustomCatalogNumbers(data.customCatalogNumbers || []);
          setKeywords(data.keywords || []);
          setStepNotes(mergeOldNotes(data.stepNotes || {}));
          const hasInventoryNumber = (data.customCatalogNumbers || []).length > 0;
          const hasKeywordsResult = (data.keywords || []).length > 0;
          markStepComplete('catalogKeywords', 0, hasInventoryNumber);
          markStepComplete('catalogKeywords', 1, hasKeywordsResult);
        }
        setIsLoadingInitialData(false);
      }).catch((err) => {
        logger.error('CatalogKeywordsTab: Failed to load keywords on retry', err);
        setHasError(true);
        setIsLoadingInitialData(false);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasError, effectiveCatalogId]);

  if (hasError) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" color="error">
          Something went wrong loading the catalog keywords form.
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Please try again or contact support if the issue persists.
        </Typography>
        <Button
          variant="outlined"
          sx={{ mt: 2 }}
          onClick={() => {
            setHasError(false);
            loadedCatalogIdRef.current = null;
          }}
        >
          Retry
        </Button>
      </Box>
    );
  }

  const availableSuggestions = suggestions.filter(
    (s) => !keywords.some((k) => k.toLowerCase() === s.toLowerCase())
  );

  return (
    <Box sx={{ p: 3 }}>
      <ImageThumbnail />
      <WizardAccordion
        id="catalog-number"
        title={en.wizard.additionalInfo.catalogNumber.title}
        subtitle={en.wizard.additionalInfo.catalogNumber.subtitle}
        defaultExpanded={true}
        privacyFieldPath="catalogKeywords.aliCatalogNumber"
      >
        <Box sx={{ mb: 2 }}>
          <DynamicTextFields
            label={en.wizard.additionalInfo.catalogNumber.catalogLabel}
            addButtonText={en.wizard.additionalInfo.catalogNumber.addButton}
            initialValues={customCatalogNumbers}
            onChange={(values) => {
              setCustomCatalogNumbers(values);
              debouncedSave();
            }}
          />
        </Box>
      </WizardAccordion>

      {!hideKeywords && (
      <WizardAccordion
        id="catalog-keywords"
        title={en.wizard.additionalInfo.keywords.title}
        subtitle={en.wizard.additionalInfo.keywords.subtitle}
        defaultExpanded={true}
        privacyFieldPath="catalogKeywords.keywords"
      >
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <Autocomplete
              freeSolo
              options={availableSuggestions}
              inputValue={keywordInput}
              onInputChange={(_event, newValue, reason) => {
                if (reason !== 'reset') {
                  // Allow commas in the input — they will be parsed when the user presses Enter or clicks Add
                  setKeywordInput(newValue);
                }
              }}
              onChange={(_event, value) => {
                if (typeof value === 'string' && value.trim()) {
                  const parts = value.split(',').map((p) => p.trim()).filter(Boolean);
                  const newKeywords = parts.reduce((acc, part) => {
                    if (!acc.some((k) => k.toLowerCase() === part.toLowerCase())) {
                      return [...acc, part];
                    }
                    return acc;
                  }, keywords);
                  if (newKeywords.length !== keywords.length) {
                    setKeywords(newKeywords);
                    debouncedSave();
                  }
                  setKeywordInput('');
                }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addKeyword();
                }
              }}
              filterOptions={(options, state) =>
                state.inputValue
                  ? options.filter((opt) =>
                      opt.toLowerCase().includes(state.inputValue.toLowerCase())
                    )
                  : options
              }
              loading={suggestionsLoading}
              fullWidth
              clearOnBlur={false}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={en.wizard.additionalInfo.keywords.label}
                  helperText={en.wizard.additionalInfo.keywords.helperText}
                  variant="outlined"
                  slotProps={{
                    input: {
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {suggestionsLoading ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    },
                  }}
                />
              )}
            />
            <Button
              variant="outlined"
              onClick={addKeyword}
              disabled={!keywordInput.trim()}
            >
              {en.wizard.additionalInfo.keywords.addButton}
            </Button>
          </Box>

          {keywords.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {keywords.map((keyword, index) => (
                <Chip
                  key={index}
                  label={keyword}
                  onDelete={() => removeKeyword(index)}
                  deleteIcon={<Close />}
                  variant="outlined"
                />
              ))}
            </Box>
          )}
        </Box>
      </WizardAccordion>
      )}

      {/* Step 3: Collection Catalogs */}
      <WizardAccordion
        id="catalog-collections"
        title={en.catalogMembership.title}
        subtitle={en.catalogMembership.subtitle}
        defaultExpanded={true}
      >
        <CollectionMembershipSelector
          itemId={effectiveCatalogId}
          onChange={handleCatalogChange}
        />
      </WizardAccordion>

      {!hideAdditionalNotes && (
        <WizardAccordion
          title={en.wizard.additionalNotes.title}
          subtitle={en.wizard.additionalNotes.subtitle}
          privacyFieldPath="catalogKeywords.notes"
        >
          <DynamicTextFields
            label={en.wizard.describe.additionalNotes.label}
            addButtonText={en.wizard.describe.additionalNotes.addButton}
            placeholder={en.wizard.describe.additionalNotes.placeholder}
            initialValues={stepNotes.notes || []}
            onChange={(notes) => {
              setStepNotes(prev => ({ ...prev, notes }));
              debouncedSave();
            }}
          />
        </WizardAccordion>
      )}
    </Box>
  );
};
